import React, { useState, useEffect, useContext, useRef } from 'react';
import './UserUpdateModal.css';
import Modal from '../Modal/Modal';
import TextInput from '../TextInput/TextInput';
import PhoneInput from '../PhoneInput/PhoneInput';
import EmailInput from '../EmailInput/EmailInput';
import SelectInput from '../SelectInput/SelectInput';
import CheckboxInput from '../CheckboxInput/CheckboxInput';
import GroupMultiSelect from '../GroupMultiSelect/GroupMultiSelect';
import SearchSelect from '../SearchSelect/SearchSelect';
import CorporationCard from '../CorporationCard/CorporationCard';
import FacilityCard from '../FacilityCard/FacilityCard';
import { userStatusMap } from '../../common/objectMaps';
import userFields from '../../common/userFields';
import ActionButtons from '../ActionButtons/ActionButtons';
import Spinner from '../Spinner/Spinner';
import MIUserCard from '../MIUserCard/MIUserCard';
import SimpleMIUserCard from '../SimpleMIUserCard/SimpleMIUserCard';
import convenienceApi from '../../common/api/convenience';
import baseApi from '../../common/api/base';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import { AdminContext } from '../../contexts/AdminContext';
import authentication from '../../B2CAuth';
import ChangeEmailModal from '../ChangeEmailModal/ChangeEmailModal';
import logComponent from '../../common/logComponent';
import lowerCaseFirstLetter from '../../common/lowerCaseFirstLetter';
//import { useMsal } from "@azure/msal-react";


export default function UserUpdateModal({ hide, isVisible, user, reloadGroups: propsReloadGroups, deleteUser, updateUser: propsUpdateUser, adminUser: propsAdminUser }) {
  //const { instance } = useMsal();
  const environmentContext = useContext(EnvironmentContext);
  const adminContext = useContext(AdminContext);
  const { hasToken, activeUser: adminUser, selectedImpersonationUser } = environmentContext;

  const [validating, setValidating] = useState(false);
  const [isValid, setIsValid] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const [status, setStatus] = useState('initial');
  const [groups, setGroups] = useState(null);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState(false);
  const [surveys, setSurveys] = useState([]);
  const [surveysLoading, setSurveysLoading] = useState(true);
  const [surveysError, setSurveysError] = useState(false);
  const [monitoredIndividualsLoading, setMonitoredIndividualsLoading] = useState(false);
  const [monitoredIndividualsError, setMonitoredIndividualsError] = useState(false);
  const [data, setData] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState(true);
  const [facilityUpdated, setFacilityUpdated] = useState(false);
  const [corporationUpdated, setCorporationUpdated] = useState(false);
  const [modalShouldExit, setModalShouldExit] = useState(false);
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const [dataWasNull, setDataWasNull] = useState(true);
  const firstRun = useRef(true);
  const facilityChangeLog = useRef([]);


  const [updatedUser, setUpdatedUser] = useState({
    legalFirstName: null,
    preferredFirstName: null,
    lastName: null,
    emailAddress: null,
    phoneNumber: null,
    userType: null,
    facility: null,
    corporation: null,
    monitoredIndividualUsers: null,
    accessLevel: null,
    canCorrespond: null,
    canImpersonate: null,
    canViewSurveyResponses: null,
    groups: null,
    status: null,
    checkInSurveyId: null
  });


  const updatedUserRef = useRef({
    legalFirstName: null,
    preferredFirstName: null,
    lastName: null,
    emailAddress: null,
    phoneNumber: null,
    userType: null,
    facility: null,
    corporation: null,
    monitoredIndividualUsers: null,
    accessLevel: null,
    canCorrespond: null,
    canImpersonate: null,
    canViewSurveyResponses: null,
   groups: null,
    status: null,
    checkInSurveyId: null
  });


  const setUpdatedUserRef = (el) => {
    if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal setUpdatedUserRef'); }
    updatedUserRef.current = el;
  }

  const modalRef = useRef(null);
  const firstTabRef = useRef(null);
  const actionButtons = useRef(null);
  const dataLoadedRef = useRef(false);

  const setDataLoadedRef = (el) => {
    if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal setDataLoadedRef'); }
    dataLoadedRef.current = el;
  }

  if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal rendered'); }


  useEffect(() => {
    if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal useEffect [environmentContext.hasToken]'); }
    if (!hasToken) return;
    if(!dataLoadedRef.current)
      loadData();
  }, [environmentContext.hasToken])


  useEffect(() => {
    if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal useEffect [updatedUser.facility]'); }
    // handle facility change
    if (!hasToken) return;
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    if (updatedUser.facility && updatedUser.facility.value) {
      //TODO:  Put in logic to reload groups upon user type change when we allow this feature.
      // For facility user
      if (user.groupTypeString === 'Facility') {
        reloadGroups();
        reloadCheckInSurveys();

        if (updatedUser.facility.value.length < 1) {
            facilityChangeLog.current.push({ facilityId: null })
        } else {
            facilityChangeLog.current.push(updatedUser.facility.value[0]);
        }

        if (facilityChangeLog.current.length > 1
            && facilityChangeLog.current[facilityChangeLog.current.length - 1].facilityId !== facilityChangeLog.current[facilityChangeLog.current.length - 2].facilityId) {
            setFacilityUpdated(true);
        }        
      }

      // For family user
      if (user.groupTypeString === 'Family') {
        reloadMonitoredIndividuals();
      }
    }
  }, [updatedUser.facility])

  useEffect(() => {
    if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal useEffect [status]'); }
    if (!hasToken) return;
    if (status === 'failure') {
      modalRef.current.scrollTop = 0;
    }

  }, [status])


  useEffect(() => {
    if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal useEffect [data]'); }
    if (!hasToken) return;
    if (dataWasNull === true) {
      setDataWasNull(false);
      focusFirstItem();
    }

  }, [data])

  const loadData = () => {
    if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal loadData'); }
    if (!hasToken) return;

    setDataLoading(true);
    setDataError(false);
    setSurveys(null);
    setSurveysLoading(true);
    setSurveysError(false);

    convenienceApi.initializeUserUpdateModal(adminUser, user)
      .then(
        result => {
          if (adminUser.groupTypeString === 'Status Solutions' &&
            user.groupTypeString === 'Corporate' &&
            !adminContext.isFresh('corporations')) {
            baseApi.Corporation.GetAll()
              .then(
                corporations => {
                  adminContext.setCorporations(corporations.data);

                  setData(result);
                  setDataLoading(false);
                  setDataError(false);
                  setDataLoadedRef(true)
                },
                error => {
                  setDataLoading(false);
                  setDataError(true);
                }
              )
          } else if (adminUser.groupTypeString === 'Status Solutions' &&
            (user.groupTypeString === 'Facility' || user.groupTypeString === 'Family') &&
            !adminContext.isFresh('facilities')) {
            baseApi.Facility.GetAll()
              .then(
                facilities => {
                  adminContext.setFacilities(facilities.data);

                  setData(result);
                  setDataLoading(false);
                  setDataError(false);

                  if (user.groupTypeString === 'Facility') {
                    let defaultChoice = [{ key: '', value: '' }];
                    let availableSurveys = result.potentialSurveys.map(survey => {
                      return {
                        key: survey.surveyName + ' (' + survey.ownerName + ')', 
                        value: survey.surveyId
                      };
                    });
                    let newSurveys = defaultChoice.concat(availableSurveys);
                    setSurveys(newSurveys);
                    setSurveysLoading(false);
                    setSurveysError(false);
                    setDataLoadedRef(true)
                  }
                },
                error => {
                  setDataLoading(false);
                  setDataError(true);
                }
              )
          } else {
            setData(result);
            setDataLoading(false);
            setDataError(false);

            if (user.groupTypeString === 'Facility') {
              let defaultChoice = [{ key: '', value: '' }];
              let availableSurveys = result.potentialSurveys.map(survey => {
                return {
                  key: survey.surveyName + ' (' + survey.ownerName + ')',
                  value: survey.surveyId
                };
              });
              let newSurveys = defaultChoice.concat(availableSurveys);
              setSurveys(newSurveys);
              setSurveysLoading(false);
              setSurveysError(false);
              setDataLoadedRef(true)
            }
          }
        },
        error => {
          setDataLoading(false);
          setDataError(true);
        }
      )
  }
  const reloadCheckInSurveys = () => {
    if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal reloadCheckInSurveys'); }
    if (!hasToken) return;

    let facilityId;

    if (updatedUser.facility && updatedUser.facility.value && updatedUser.facility.value.length > 0) {
      facilityId = updatedUser.facility.value[0].facilityId
    }


    if (!facilityId) {
      let newData = Object.assign({}, data);
      newData.potentialSurveys = [];
      newData.checkInSurveyId = null;
      setData(newData);
      return;
    }

    setSurveysLoading(true);
    setSurveysError(false);
    baseApi.Survey.GetAllCheckInSurveysForFacility(facilityId)
      .then(
        result => {
          let defaultChoice = [{ key: '', value: '' }];
          let availableSurveys = result.data.map(survey => {
            return {
              key: survey.surveyName + ' (' + survey.ownerName + ')',
              value: survey.surveyId
            };
          });
          let newSurveys = defaultChoice.concat(availableSurveys);
          setSurveys(newSurveys);
          setSurveysLoading(false);
          setSurveysError(false);
          let newData = Object.assign({}, data);
          newData.potentialSurveys = result.data;
          newData.checkInSurveyId = null;
          setData(newData);

        },
        error => {
          setSurveys(null);
          setSurveysLoading(false);
          setSurveysError(true);
        }
      );
  }


  const loadGroupMemberships = () => {
    if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal loadGroupMemberships'); }
    //TODO:  What to put here.  Method was missing
  }

  const reloadGroups = () => {
    if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal reloadGroups'); }
    if (!hasToken) return;

    let newFacilityId = null;


    let adminUserType = propsAdminUser.groupTypeString;
    let newUserType;

    switch (adminUserType) {
      case 'Status Solutions':
      case 'Facility':
        newUserType = (updatedUser.userType ? updatedUser.userType.value : null)
        break;
      case 'Corporate':
        newUserType = 'Corporate';
        break;
      case 'Family':
        newUserType = 'Family';
        break;
      default:
        break;
    }

    setGroups(null);
    setGroupsLoading(true);
    setGroupsError(false);

    let id = 0;
    if (newUserType) {
      switch (newUserType) {
        case 'Status Solutions':
          // Get Status Solutions Id to load groups from
          id = propsAdminUser.statusSolutionsId;
          if (id) {
            baseApi.StatusSolutions.GetAllGroups(id)
              .then(
                result => {

                  let newData = data;
                  newData.potentialGroups = result.data;
                  setData(newData);
                  setGroupsLoading(false);
                  setGroupsError(false);


                },
                error => {
                  let newData = data;
                  newData.potentialGroups = [];
                  newData.selectedGroupIds = [];
                  setData(newData);
                  setGroupsLoading(false);
                  setGroupsError(true);

                }
              );

          } else {
            let newData = Object.assign({}, data);
            newData.potentialGroups = [];
            newData.selectedGroupIds = [];
            setData(newData);


            return;
          }
          break;
        case 'Corporate':
          // Determine correct Corporation to load groups from
          id =
            updatedUserRef.current.corporation &&
            updatedUserRef.current.corporation.value &&
            updatedUserRef.current.corporation.value.length > 0 &&
            updatedUserRef.current.corporation.value[0].corporationId;

          if (id) {
            baseApi.Corporation.GetAllGroups(id)
              .then(
                result => {
                  let newData = data;
                  newData.potentialGroups = result.data;
                  setData(newData);
                  setGroupsLoading(false);
                  setGroupsError(false);

                },
                error => {
                  let newData = data;
                  newData.potentialGroups = [];
                  newData.selectedGroupIds = [];

                  setData(newData);
                  setGroupsLoading(false);
                  setGroupsError(true);
                }
              );

          } else {
            let newData = Object.assign({}, data);
            newData.potentialGroups = [];
            newData.selectedGroupIds = [];
            setData(newData);


            return;
          }
          break;
        case 'Facility':
          // Determine correct Facility to load groups from
          id =
            updatedUserRef.current.facility &&
            updatedUserRef.current.facility.value &&
            updatedUserRef.current.facility.value.length > 0 &&
            updatedUserRef.current.facility.value[0].facilityId;
          if (id) {
            baseApi.Facility.GetAllGroups(id)
              .then(
                result => {
                  let newData = data;
                  newData.potentialGroups = result.data;
                  setData(newData);
                  setGroupsLoading(false);
                  setGroupsError(false);

                },
                error => {
                  let newData = data;
                  newData.potentialGroups = [];
                  newData.selectedGroupIds = [];
                  setData(newData);
                  setGroupsLoading(false);
                  setGroupsError(true);
                }
              );

          } else {

            let newData = Object.assign({}, data);
            newData.potentialGroups = [];
            newData.selectedGroupIds = [];
            setData(newData);

            return;
          }
          break;
        case 'Family':
          break;
        default:
          break;
      }
    }
  }

  const reloadMonitoredIndividuals = () => {
    if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal reloadMonitoredIndividuals'); }
    if (!hasToken) return;

    let facilityId;

    if (updatedUser.facility && updatedUser.facility.value && updatedUser.facility.value.length > 0) {
      facilityId = updatedUser.facility.value[0].facilityId
    }

    if (!facilityId) {
      let newData = Object.assign({}, data);
      newData.potentialMonitoredIndividualUsers = [];
      newData.selectedMonitoredIndividualUsers = [];
      setData(newData);
      return;
    }

    setMonitoredIndividualsLoading(true);
    setMonitoredIndividualsError(false);

    baseApi.Facility.GetAllMonitoredIndividuals(facilityId)
      .then(
        result => {
          let monitoredIndividuals = result.data;

          // Change MonitoredIndividuals into MonitoredIndividualUsers
          let monitoredIndividualUsers = monitoredIndividuals.map(monitoredIndividual => {
            return {
              monitoredIndividualId: monitoredIndividual.monitoredIndividualId,
              monitoredIndividual: monitoredIndividual,
              userId: user.userId,
              isAdmin: null,
              selected: false
            };
          })

          let newData = Object.assign({}, data);
          newData.potentialMonitoredIndividualUsers = monitoredIndividualUsers;
          newData.selectedMonitoredIndividualUsers = [];
          setData(newData);
          setMonitoredIndividualsLoading(false);
          setMonitoredIndividualsError(false);

        },
        error => {
          let newData = Object.assign({}, data);
          newData.potentialMonitoredIndividualUsers = [];
          newData.selectedMonitoredIndividualUsers = [];
          setData(newData);
          setMonitoredIndividualsLoading(false);
          setMonitoredIndividualsError(true);
        }
      );
  }

  const showValidationErrors = () => {
    if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal showValidationErrors'); }
    setShowErrors(true);
  }

  const hideValidationErrors = () => {
    if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal hideValidationErrors'); }
    setShowErrors(false);
  }

  const validateField = (field) => {
    if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal validateField'); }
    if (field.name === 'monitoredIndividualUsers' && field.value) {
      handleMonitoredIndividualUpdate(field.value);
    }    

    let newUpdatedUser = { ...updatedUserRef.current };
    newUpdatedUser[lowerCaseFirstLetter(field.name)] = field;
    setUpdatedUserRef(newUpdatedUser);

    setUpdatedUser(newUpdatedUser); //doing this as both ref and state so it will have the values updated when I need but still refresh the page when needed
  }

  const handleMonitoredIndividualUpdate = (selectedMIUsers) => {
    if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal handleMonitoredIndividualUpdate'); }
    const { potentialMonitoredIndividualUsers: potentialMIUsers } = data;

    if (!potentialMIUsers) return;

    // go through each mnitored individual and set Selected prop
    let updatedMIUsers = data.potentialMonitoredIndividualUsers.map(potentialMIUser => {
      let updatedMIUser = Object.assign({}, potentialMIUser);
      if (selectedMIUsers.some(selectedMIUser => updatedMIUser.monitoredIndividualId === selectedMIUser.monitoredIndividualId)) {
        updatedMIUser.selected = true;
      } else {
        updatedMIUser.selected = false;
        updatedMIUser.isAdmin = null;
        updatedMIUser.needsAccessLevel = false;
      }

      return updatedMIUser;
    });

    let newData = Object.assign({}, data);
    newData.potentialMonitoredIndividualUsers = updatedMIUsers;
    setData(newData);
  }

  const changeEmailConfirm = () => {
    if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal changeEmailConfirm'); }
    setShowChangeEmailModal(false);
    handleValidSubmission();
  }

  const changeEmailCancel = () => {
    if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal changeEmailCancel'); }
    setShowChangeEmailModal(false);
  }

  const submit = () => {
    if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal submit'); }
    let emailChanged = user.emailAddress && updatedUser.emailAddress && user.emailAddress !== updatedUser.emailAddress.value;

    setTimeout(() => {
      // check all necessary fields for adminUser and user type
      let requiredFieldList = getRequiredFieldList();

      if (!requiredFieldList) {
        handleInvalidSubmission();
        return;
      }

      let isValid = requiredFieldList.reduce((prev, curr) => prev && updatedUser[curr] && updatedUser[curr].isValid, true);

      // Check whether any MonitoredIndividualUsers don't have their IsAdmin prop set
      if (updatedUser.monitoredIndividualUsers && updatedUser.monitoredIndividualUsers.value.length > 0) {
        updatedUser.monitoredIndividualUsers.value = updatedUser.monitoredIndividualUsers.value.map(MIUser => {
          let potentialMIUser = data.potentialMonitoredIndividualUsers.find(potentialMIUser => {
            return potentialMIUser.monitoredIndividualId === MIUser.monitoredIndividualId;
          });

          MIUser.isAdmin = potentialMIUser.isAdmin;
          return MIUser;
        });
      }

      if (updatedUser.monitoredIndividualUsers &&
        updatedUser.monitoredIndividualUsers.value.length > 0 &&
        updatedUser.monitoredIndividualUsers.value.some(MIUser => MIUser.isAdmin == null)) {
        isValid = false;
      }

      if (!isValid) {
        handleInvalidSubmission();
      } else {
        if (emailChanged) {
          setShowChangeEmailModal(true);
        } else {
          handleValidSubmission();
        }
      }
    }, 1000);
  }

  const handleValidSubmission = () => {
    if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal handleValidSubmission'); }
    hideValidationErrors();
    updateUser();
    setIsValid(true);
  }

  const handleInvalidSubmission = () => {
    if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal handleInvalidSubmission'); }

    // go through each monitored individual, and for each one lacking an IsAdmin prop...
    if (!updatedUser.monitoredIndividualUsers || updatedUser.monitoredIndividualUsers.value.length === 0) return {};

    let newPotentialMIUsers = data.potentialMonitoredIndividualUsers.map(potentialMIUser => {
      if (updatedUser.monitoredIndividualUsers.value.some(MIUser => {
        return MIUser.isAdmin == null &&
          MIUser.monitoredIndividualId === potentialMIUser.monitoredIndividualId;
      })) {
        potentialMIUser.needsAccessLevel = true;
      } else {
        potentialMIUser.needsAccessLevel = false;
      }

      return potentialMIUser;
    });

    let newData = Object.assign({}, data);
    newData.potentialMonitoredIndividualUsers = newPotentialMIUsers;
    setData(newData);

    showValidationErrors();
    modalRef.current.scrollTop = 0;
    focusFirstItem();
    setIsValid(false);
    reset(); // necessary?
  }

  const getRequiredFieldList = () => {
    if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal getRequiredFieldList'); }

    let adminUserType = adminUser.groupTypeString;
    let updatedUserType = user.groupTypeString;

    if (userFields[adminUserType] && userFields[adminUserType][updatedUserType]) {
      return userFields[adminUserType][updatedUserType].concat('status');
    } else {
      return null;
    }
  }

  const updateUser = () => {
    if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal updateUser'); }
    if (!hasToken) return;

    let userToSubmit = {};
    let noRemainingMIUsers;

    let emailChanged = user.emailAddress && updatedUser.emailAddress && user.emailAddress !== updatedUser.emailAddress.value;
    let checkInSurveyChanged = (user.checkInSurveyId != null && updatedUser.checkInSurveyId == null) || (user.checkInSurveyId == null && updatedUser.checkInSurveyId != null) || (user.checkInSurveyId && updatedUser.checkInSurveyId && user.checkInSurveyId !== updatedUser.checkInSurveyId);


    user.groups = data.selectedGroupIds;
    user.checkInSurveyId = data.checkInSurveyId;

    setStatus('pending');

    let updatedUserEntries = Object.entries(updatedUser);

    updatedUserEntries.forEach(entry => {
      userToSubmit[entry[0]] = entry[1] ? entry[1].value : null;
    })

    // Add/Modify fields as necessary
    userToSubmit.externalId = user.externalId;
    userToSubmit.userId = user.userId;
    userToSubmit.canCorrespond = userToSubmit.canCorrespond == null ? false : userToSubmit.canCorrespond;
    userToSubmit.canImpersonate = userToSubmit.canImpersonate == null ? false : userToSubmit.canImpersonate;
    userToSubmit.canViewSurveyResponses = userToSubmit.canViewSurveyResponses == null ? false : userToSubmit.canViewSurveyResponses;

    switch (user.groupTypeString) {
      case 'Status Solutions':
        userToSubmit.isAdmin = userToSubmit.accessLevel === 'Admin';
        userToSubmit.statusSolutionsId = user.statusSolutionsId;
        break;
      case 'Corporate':
        userToSubmit.isAdmin = userToSubmit.accessLevel === 'Admin';
        userToSubmit.corporationId = userToSubmit.corporation ? userToSubmit.corporation[0].corporationId : user.corporationId
        break;
      case 'Facility':
        userToSubmit.isAdmin = userToSubmit.accessLevel === 'Admin';
        userToSubmit.facilityId = userToSubmit.facility ? userToSubmit.facility[0].facilityId : user.facilityId
        break;
      case 'Family':
        // For each MIUser in userToSubmit, read IsAdmin from corresponding MIUser in data.potentialMonitoredIndividualUsers
        userToSubmit.monitoredIndividualUsers = userToSubmit.monitoredIndividualUsers.map(MIUser => {
          let potentialMIUser = data.potentialMonitoredIndividualUsers.find(potentialMIUser => {
            return potentialMIUser.monitoredIndividualId === MIUser.monitoredIndividualId;
          });

          MIUser.isAdmin = potentialMIUser.isAdmin;

          return MIUser;
        });

        // Handle reactivation of a user by SS adminUser
        if (adminUser.groupTypeString === 'Status Solutions' &&
          userToSubmit.monitoredIndividualUsers.length > 0 &&
          user.status === 'Inactive') {
          userToSubmit.status = 'Active';
        }

        // If there are no MonitoredIndividuals left, record this so user can be removed from parent list
        noRemainingMIUsers = userToSubmit.monitoredIndividualUsers.length === 0;
        break;
      default:
        break;
    }

    // Add back in externalMonitoredIndividualUsers, if there are any
    if (data.externalMonitoredIndividualUsers && data.externalMonitoredIndividualUsers.length > 0) {
      userToSubmit.monitoredIndividualUsers = userToSubmit.monitoredIndividualUsers.concat(data.externalMonitoredIndividualUsers);
    }

    // This may not be necessary, but deleting these keys anyway
    delete userToSubmit.accessLevel;
    delete userToSubmit.userType;
    delete userToSubmit.corporation;
    delete userToSubmit.facility;

    convenienceApi.updateUser(user, userToSubmit)
      .then(
        result => {
          setStatus('success');

          // Remove user from UserManagement list, if necessary
          if ((noRemainingMIUsers || userToSubmit.status === 'Inactive') &&
            adminUser.groupTypeString !== 'Status Solutions') {
            userToSubmit.groupTypeString = result.groupTypeString;
            deleteUser(userToSubmit);
          } else {
            propsUpdateUser(result);
          }

          // If the updated user is the environmentContext user, update that
          if (result.userId === environmentContext.activeUser.userId) {
            if (checkInSurveyChanged) {
              environmentContext.setShouldUpdateCheckInSurvey(true);
            }
            if (emailChanged) {
              authentication.signOut();
              //  instance.signOut();
            } else {

              if (selectedImpersonationUser) {
                environmentContext.setSelectedImpersonationUser(result);
              } else {
                environmentContext.setUser(result);
              }
            }
          }

          startModalExit();
        },
        error => {
          setStatus('failure');
        }
      )
  }

  const reset = () => {
    if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal reset'); }
    setStatus('initial');
  }

  const focusFirstItem = () => {
    if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal focusFirstItem'); }
    if (firstTabRef && firstTabRef.current) firstTabRef.current.focus();
  }

  const handleShiftTab = (e) => {
    if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal handleShiftTab'); }
    const { key, shiftKey } = e;

    if (key !== 'Tab' || !shiftKey) return;

    e.preventDefault();
    if (actionButtons && actionButtons.current) actionButtons.current.focus();
  }

  const startModalExit = () => {
    if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal startModalExit'); }
    setModalShouldExit(true);
  }

  const handleKeyDown = (e) => {
    if (logComponent('UserUpdateModal')) { console.debug('UserUpdateModal handleKeyDown'); }
    if (e.key === 'Escape') startModalExit();
  }

  const formIsInvalid = isValid === false;
  const monitoredIndividualsSelected =
    updatedUser.monitoredIndividualUsers &&
    updatedUser.monitoredIndividualUsers.value &&
    updatedUser.monitoredIndividualUsers.value.length > 0;
  let emailChanged = user.emailAddress && updatedUser.emailAddress && user.emailAddress !== updatedUser.emailAddress.value;
  let checkInSurveyChanged = (user.checkInSurveyId != null && updatedUser.checkInSurveyId == null) || (user.checkInSurveyId == null && updatedUser.checkInSurveyId != null) || (user.checkInSurveyId && updatedUser.checkInSurveyId && user.checkInSurveyId !== updatedUser.checkInSurveyId);
  let currentUser = adminUser.userId === updatedUser.userId;

  return (
    <Modal isVisible={isVisible} shouldExit={modalShouldExit} hide={hide} ref={modalRef} remainOnBackdropClick={true}>
      <div
        className="User-Update-Modal"
        onKeyDown={handleKeyDown}>
        <h3 className="User-Update-Modal__heading">Modify user</h3>
        <p className={'User-Update-Modal__page-error' + (showErrors ? ' visible' : '')}>There were some errors. Please double check the information and resubmit.</p>
        <p className={'User-Update-Modal__page-error' + (status === 'failure' ? ' visible' : '')}>Sorry, something went wrong.  Changes were not saved.  Please try again.</p>
        {
          data &&
          <form className="User-Update-Modal__form">
            <TextInput
              initialValue={user.legalFirstName}
              label="First name (legal)"
              name="legalFirstName"
              rules={{ required: true }}
              errorMessages={{ required: 'Please provide the user\'s legal first name' }}
              showError={formIsInvalid}
              validate={validating}
              onValidate={validateField}
              ref={firstTabRef}
              onKeyDown={handleShiftTab} />
            <TextInput
              initialValue={user.preferredFirstName}
              label="First name (preferred)"
              name="preferredFirstName"
              instructions="If applicable"
              validate={validating}
              onValidate={validateField} />
            <TextInput
              initialValue={user.lastName}
              label="Last name"
              name="lastName"
              rules={{ required: true }}
              errorMessages={{ required: 'Please provide the user\'s last name' }}
              showError={formIsInvalid}
              validate={validating}
              onValidate={validateField} />
            <EmailInput
              initialValue={user.emailAddress}
              label="Email address"
              name="emailAddress"
              tabIndex={-1}
              rules={{ required: true }}
              errorMessages={[
                {
                  rules: ['required', 'validEmail'],
                  text: 'Please provide a valid email address for the user'
                },
                {
                  rules: ['unusedEmail'],
                  text: 'This email address is already in use'
                }
              ]}
              showError={formIsInvalid}
              validate={validating}
              onValidate={validateField}
              requireEmailUniqueness={true} />
            <PhoneInput
              initialValue={user.phoneNumber}
              label="Phone number"
              name="phoneNumber"
              rules={{ maxLength: 10, required: true }}
              errorMessage="Please provide a valid phone number for the user"
              showError={formIsInvalid}
              validate={validating}
              onValidate={validateField} />
            {
              adminUser && adminUser.groupTypeString !== 'Family' &&
              <SelectInput
                initialValue={user.groupTypeString}
                label="User type"
                name="userType"
                items={[user.groupTypeString]}
                errorMessage="Please select a user type"
                disabled={true}
                instructions="User type cannot be changed.  If necessary, you may create a separate family account for this individual."
                showError={formIsInvalid}
                rules={{ required: true }}
                validate={validating}
                onValidate={validateField}
              />
            }
            {
              adminUser.groupTypeString === 'Status Solutions' &&
              user.groupTypeString === 'Corporate' &&
              <SearchSelect
                label="Select corporation"
                selectionMode="search"
                maximumSelections={1}
                itemKeyField="corporationId"
                itemPluralTerm="corporations"
                items={adminContext.corporations}
                itemComponent={CorporationCard}
                itemComponentPropName="corporation"
                selectedItems={data.selectedCorporations}
                searchFieldGroups={[
                  ['name']
                ]}
                name="corporation"
                rules={{
                  numItems: 1, required: true
                }}
                showError={formIsInvalid}
                errorMessage="Please select a corporation"
                validate={validating}
                onValidate={validateField}
                instructions="Only one corporation can be selected." />
            }
            {
              adminUser.groupTypeString === 'Status Solutions' &&
              (user.groupTypeString === 'Facility' || user.groupTypeString === 'Family') &&
              <SearchSelect
                label="Select facility"
                selectionMode="search"
                maximumSelections={1} //TODO: Put this back in once we allow users across facilities.  //{(user.groupTypeString === 'Facility' ? 1 : null) }
                itemKeyField="facilityId"
                itemPluralTerm="facilities"
                items={adminContext.facilities}
                selectedItems={data.selectedFacilities}
                itemComponent={FacilityCard}
                itemComponentPropName="facility"
                searchFieldGroups={[
                  ['name'],
                  ['salesforceSiteId']
                ]}
                name="facility"
                rules={(user.groupTypeString === 'Facility' ? { numItems: 1, required: true } : null)}
                showError={formIsInvalid}
                errorMessage="Please select a facility"
                validate={validating}
                onValidate={validateField}
                instructions="Only one facility can be selected."
              />
            }
            {
              user.groupTypeString !== 'Family' &&
              <SelectInput
                initialValue={((facilityUpdated || corporationUpdated) ? 'Standard' : (user.isAdmin ? 'Admin' : 'Standard'))}
                label="Access level"
                name="accessLevel"
                items={['Admin', 'Standard']}
                rules={{ required: true }}
                errorMessage="Please select an access level"
                showError={formIsInvalid}
                validate={validating}
                onValidate={validateField} />
            }
            <SelectInput
              initialValue={userStatusMap.get(user.status)}
              label="Status"
              name="status"
              rules={{ required: true }}
              items={['Active', 'Inactive']}
              validate={validating}
              onValidate={validateField} />
            {
              user.groupTypeString === 'Family' &&
              <SearchSelect
                selectionMode={adminUser.groupTypeString === 'Family' && adminUser.monitoredIndividualUsers.length <= 5 ? 'list' : 'search'}
                label="Select individual(s)"
                itemKeyField="monitoredIndividualId"
                itemPluralTerm="individuals"
                items={data.potentialMonitoredIndividualUsers}
                selectedItems={data.selectedMonitoredIndividualUsers}
                potentialItemComponent={SimpleMIUserCard}
                selectedItemComponent={MIUserCard}
                itemComponentPropName="miUser"
                searchFieldGroups={[
                  ['monitoredIndividual.legalFirstName', 'monitoredIndividual.lastName'],
                  ['monitoredIndividual.preferredFirstName', 'monitoredIndividual.lastName'],
                  ['monitoredIndividual.dateOfBirth']
                ]}
                name="monitoredIndividualUsers"
                rules={{}}
                showError={formIsInvalid}
                errorMessage="Please select at least one individual"
                validate={validating}
                onValidate={validateField}
              />
            }
            {
              user && user.groupTypeString === 'Facility' &&
              <CheckboxInput
                initialValue={facilityUpdated ? false : user.canCorrespond}
                label="Correspondence override"
                name="canCorrespond"
                instructions="Note: when checked, this user will always be able to correspond with family members, regardless of permissions based on group membership."
                explanation="This user may correspond with individuals' family."
                validate={validating}
                onValidate={validateField} />
            }
            {
              user && (user.groupTypeString === 'Status Solutions' || user.groupTypeString === 'Corporate') &&
              <CheckboxInput
                initialValue={corporationUpdated ? false : user.canImpersonate}
                label="Mimic override"
                name="canImpersonate"
                instructions="Note: when checked, this user will always be able to mimic other users in the MIMI system associated with their organization to assist with troubleshooting or other needs."
                explanation="This user may mimic other users in MIMI."
                validate={validating}
                onValidate={validateField} />
            }
            {
              user && user.groupTypeString === 'Facility' &&
              <CheckboxInput
                label="View SurveyResponses override"
                name="canViewSurveyResponses"
                instructions="Note: when checked, this user will always be able view survey responses in the MIMI system, regardless of permissions based on group membership."
                explanation="This user may view survey responses in MIMI."
                initialValue={facilityUpdated ? false : user.canViewSurveyResponses}
                validate={validating}
                onValidate={validateField} />
            }
            {
              user && user.groupTypeString === 'Facility' &&
              <SelectInput
                label="Check-In survey"
                name="checkInSurveyId"
                initialValue={data.checkInSurveyId}
                items={surveys}
                errorMessage="Please select a check-in survey for this user.  If not is selected, it will use the default survey"
                showError={formIsInvalid}
                rules={null}
                validate={validating}
                onValidate={validateField}
              />
            }
            {
              user.groupTypeString && user.groupTypeString !== 'Family' &&
              (
                (((adminUser.groupTypeString === 'Status Solutions') && (updatedUser.facility && updatedUser.facility.value && updatedUser.facility.value.length > 0 && updatedUser.facility.value[0].facilityId > 0)) || adminUser.groupTypeString === 'Facility') ||
                (((adminUser.groupTypeString === 'Status Solutions') && (updatedUser.corporation && updatedUser.corporation.value && updatedUser.corporation.value.length > 0 && updatedUser.corporation.value[0].corporationId > 0)) || adminUser.groupTypeString === 'Corporate') ||
                (adminUser.groupTypeString === 'Status Solutions' && updatedUser.userType && updatedUser.userType.value === 'Status Solutions')

              ) &&
              <GroupMultiSelect
                label="Group memberships"
                name="groups"
                selectedGroupIds={data.selectedGroupIds}
                reloadSelectedGroupIds={loadGroupMemberships}
                allGroups={data.potentialGroups}
                reloadGroups={propsReloadGroups}
                validate={validating}
                onValidate={validateField} />
            }
            <ActionButtons
              mainAction={submit}
              mainActionText="Save changes"
              mainActionDisabled={user.status === 'Inactive' && user.groupTypeString === 'Family' && !monitoredIndividualsSelected}
              cancel={startModalExit}
              cancelText="Cancel"
              currentStatus={status}
              ref={actionButtons} />
            <span
              tabIndex="0"
              className="User-Creation-Modal__focus-control"
              onFocus={focusFirstItem}></span>
          </form>
        }
        {
          dataLoading &&
          <div className="User-Update-Modal__loading">
            <Spinner />
          </div>
        }
        {
          dataError &&
          <div className="User-Update-Modal__error">
            <p className="User-Update-Modal__error-text">Sorry, something went wrong...</p>
            <button
              onClick={loadData}
              className="User-Update-Modal__reload-button">Reload</button>
          </div>
        }
        {showChangeEmailModal &&
          <ChangeEmailModal
            isCurrentUser={currentUser}
            onChangeEmail={changeEmailConfirm}
            onCancelChangeEmail={changeEmailCancel} />
        }
      </div>
    </Modal>
  );
}